import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import PremiumErrorScreen from './PremiumErrorScreen';
import { currentApp, currentUserId, toError } from '../lib/errorReporting';
import { signalShellSignedOut } from '../Contexts/nativeBridge';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (process.env.NODE_ENV === 'production') {
      // React 18 does not forward boundary-caught errors to window.onerror, so
      // this is the only path by which a render crash reaches Sentry.
      Sentry.captureException(toError(error), {
        user: { id: currentUserId() },
        tags: { app: currentApp(), where: 'ErrorBoundary' },
        extra: { componentStack: errorInfo.componentStack },
      });
    }
    console.error('Uncaught error:', error, errorInfo);
  }

  handleRetry = () => {
    window.location.reload();
  };

  handleLogout = () => {
    localStorage.clear();
    signalShellSignedOut();
    window.location.href = '/login';
  };

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <PremiumErrorScreen
            onRetry={this.handleRetry}
          />
        )
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
